import type { AxisAssessment, LevelAssessmentResult } from "@shared/labs/level-assessment";
import type { LevelAssessmentDebug } from "@shared/labs/level-assessment-debug";
import {
  ClipboardCheck,
  MousePointerClick,
  Printer,
  RotateCcw,
  Share2,
  Terminal,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { AxisDetail } from "./axis-detail";
import { CandidateHeader } from "./candidate-header";
import { ControlPanel } from "./control-panel";
import { DebugView } from "./debug-view";
import { ExtractedProfilePanel, type ExtractedProfileViewModel } from "./extracted-profile-panel";
import { LevelDetail } from "./level-detail";
import { type ReviewContext, ReviewPanel } from "./review-panel";
import { C, DM, UI } from "./ui-tokens";

export interface LevelAssessmentReportProps {
  candidateName: string;
  candidateSubtitle?: string;
  contextLabel: string;
  result: LevelAssessmentResult;
  extractedProfile?: ExtractedProfileViewModel;
  /** Inspection payload. Present only when the run was made with `debug=true`. */
  debug?: LevelAssessmentDebug | null;
  /** Present only for LinkedIn-sourced runs — enables the calibration review.
   *  Absent for demo/free-text runs (the server can't re-fetch + re-extract). */
  reviewContext?: ReviewContext;
  /** Id of the persisted run. When present, the "Compartilhar" button copies a
   *  link any authenticated platform user can open. Absent for demo fixtures (and
   *  the rare run whose persistence write failed). */
  evaluationId?: string;
  /** Candidate LinkedIn URL — threaded to the decisive-signal citations'
   *  "Abrir no LinkedIn" link. Null for free-text / demo runs. */
  linkedinUrl?: string | null;
  onReset: () => void;
}

/** Special selection keys (anything else is an axis id). */
type Selection = "level" | "debug" | "review" | "none" | string;

function findAxis(
  result: LevelAssessmentResult,
  id: string,
): { assessment: AxisAssessment; label: string } | null {
  const all = [...result.cross.axes, ...result.vertical.axes];
  const assessment = all.find((a) => a.axis === id);
  if (!assessment) return null;
  const labelById = new Map<string, string>();
  for (const a of result.modelSnapshot.crossAxes) labelById.set(a.id, a.label);
  for (const a of result.modelSnapshot.discipline?.axes ?? []) labelById.set(a.id, a.label);
  return { assessment, label: labelById.get(id) ?? id };
}

function FooterButton({
  children,
  icon: Icon,
  onClick,
  testId,
  active = false,
}: {
  children: React.ReactNode;
  icon: typeof Share2;
  onClick: () => void;
  testId?: string;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        ...DM,
        fontWeight: 700,
        fontSize: 13,
        padding: "8px 14px",
        borderRadius: 999,
        cursor: "pointer",
        background: active ? C.ink : C.white,
        color: active ? C.white : C.ink,
        border: `1px solid ${active ? C.ink : C.border}`,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <Icon size={15} /> {children}
    </button>
  );
}

/**
 * Candidate Painel — the redesigned evaluation result. A slim candidate header
 * (with the clickable overall-level block), two radar instruments (Cross +
 * Vertical), and detail-on-demand below (level reading, axis reading, or the
 * debug transcript). Selection is single-slot: clicking the open item closes it.
 */
export function LevelAssessmentReport(props: LevelAssessmentReportProps) {
  const { result } = props;
  const { toast } = useToast();
  // The calibration review writes shared model state — a labs/DS action. Regular
  // platform users can use LA but don't see the review affordance.
  const { hasLabsAccess } = useAuth();
  const levels = result.modelSnapshot.levels;
  const hasDebug = !!props.debug;

  const [selected, setSelected] = useState<Selection>("level");
  const detailRef = useRef<HTMLDivElement | null>(null);
  const didMount = useRef(false);

  const onSelect = (id: Selection) => setSelected((prev) => (prev === id ? "none" : id));

  // Copy the shareable link to the clipboard. Any authenticated platform user who
  // opens it sees this exact report (re-rendered from the stored snapshot).
  async function handleShare() {
    if (!props.evaluationId) return;
    const url = `${window.location.origin}/labs/level-assessment/e/${props.evaluationId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copiado",
        description: "Qualquer usuário da plataforma pode abrir esta avaliação pelo link.",
      });
    } catch {
      // Clipboard blocked (insecure context / permissions) — surface the URL.
      toast({ title: "Copie o link", description: url });
    }
  }

  // Scroll the detail into view when the selection changes (skip first mount).
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (selected === "none") return;
    detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selected]);

  const axis = useMemo(
    () =>
      selected !== "level" && selected !== "debug" && selected !== "review" && selected !== "none"
        ? findAxis(result, selected)
        : null,
    [result, selected],
  );

  return (
    <div style={{ padding: "8px 0 64px" }}>
      <div
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 22,
        }}
      >
        <CandidateHeader
          candidateName={props.candidateName}
          candidateSubtitle={props.candidateSubtitle}
          contextLabel={props.contextLabel}
          result={result}
          levelSelected={selected === "level"}
          onOpenLevel={() => onSelect("level")}
          linkedinUrl={props.linkedinUrl}
        />

        <ControlPanel
          result={result}
          selectedId={axis ? axis.assessment.axis : null}
          onSelect={onSelect}
          showConfidence
        />

        <div ref={detailRef}>
          {selected === "level" && (
            <LevelDetail result={result} onClose={() => setSelected("none")} />
          )}
          {axis && (
            <AxisDetail
              assessment={axis.assessment}
              axisLabel={axis.label}
              levels={levels}
              linkedinUrl={props.linkedinUrl}
              onClose={() => setSelected("none")}
            />
          )}
          {selected === "debug" && props.debug && <DebugView debug={props.debug} />}
          {selected === "review" && hasLabsAccess && props.reviewContext && (
            <ReviewPanel
              result={result}
              reviewContext={props.reviewContext}
              onClose={() => setSelected("none")}
            />
          )}
          {selected === "none" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                padding: 18,
                border: `1px dashed ${C.ink20}`,
                borderRadius: 14,
                color: C.ink50,
              }}
            >
              <MousePointerClick size={17} color={C.ink50} />
              <span style={{ ...UI, fontSize: 13 }}>
                Clique em uma competência ou no nível para ver as evidências.
              </span>
            </div>
          )}
        </div>

        {props.extractedProfile && <ExtractedProfilePanel profile={props.extractedProfile} />}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {hasDebug && (
            <FooterButton
              icon={Terminal}
              onClick={() => onSelect("debug")}
              active={selected === "debug"}
              testId="tab-debug"
            >
              Debug
            </FooterButton>
          )}
          {hasLabsAccess && props.reviewContext && (
            <FooterButton
              icon={ClipboardCheck}
              onClick={() => onSelect("review")}
              active={selected === "review"}
              testId="labs-la-review"
            >
              Revisar avaliação
            </FooterButton>
          )}
          {props.evaluationId && (
            <FooterButton icon={Share2} onClick={handleShare} testId="labs-la-share">
              Compartilhar
            </FooterButton>
          )}
          <FooterButton icon={Printer} onClick={() => window.print()} testId="labs-la-export">
            Exportar
          </FooterButton>
          <FooterButton icon={RotateCcw} onClick={props.onReset} testId="labs-la-reset">
            Nova avaliação
          </FooterButton>
        </div>
      </div>
    </div>
  );
}
